import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { Area } from "../../administration/admin-domain.js";
import type { Client } from "../../domain/client/client.js";

export default class AdminAreasPage extends HTMLElement {
  private areas: Area[] = [];
  private clients: Client[] = [];
  private searchQuery = "";
  private statusFilter = "ALL";
  private clientFilter = "ALL";
  private isModalOpen = false;
  private editingArea: Area | null = null;
  private modalErrorMessage: string | null = null;
  private deleteModal = {
    isOpen: false,
    id: "",
    name: "",
    clientId: "",
    title: "",
    message: "",
    action: "delete" as "delete" | "inactivate",
    error: null as string | null
  };

  public connectedCallback(): void {
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId || null,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      const [cls, ars] = await Promise.all([
        platform.adminService.listAuthorizedClients(dummyContext).catch(() => []),
        platform.adminService.listAreas(dummyContext).catch(() => [])
      ]);
      this.clients = Array.isArray(cls) ? cls : [];
      this.areas = Array.isArray(ars) ? ars : [];
      this.render();
    } catch (e) {
      console.error("Failed to load areas", e);
    }
  }

  private render(): void {
    const safeClients = Array.isArray(this.clients) ? this.clients : [];
    const safeAreas = Array.isArray(this.areas) ? this.areas : [];
    const clientMap = new Map(safeClients.map(c => [c.id, c.name]));

    const filtered = safeAreas.filter(a => {
      const matchesClient = this.clientFilter === "ALL" || a.clientId === this.clientFilter;
      const matchesStatus = this.statusFilter === "ALL" || a.status === this.statusFilter;
      const term = this.searchQuery.toLowerCase();
      const clientName = (clientMap.get(a.clientId) || '').toLowerCase();
      const matchesSearch = !term ||
        a.name.toLowerCase().includes(term) ||
        clientName.includes(term) ||
        (a.description && a.description.toLowerCase().includes(term));
      return matchesClient && matchesStatus && matchesSearch;
    });

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px;">
          <div>
            <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Gestão de Áreas Operacionais</h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Cadastro e controle de áreas e setores técnicos por cliente.</p>
          </div>
          <button id="btn-new-area" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
            <span>+ Nova Área</span>
          </button>
        </header>

        <!-- Filters -->
        <div style="display: flex; gap: 12px; background: var(--color-surface-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); align-items: center; flex-wrap: wrap;">
          <input type="text" id="input-search" placeholder="Buscar por área, cliente ou descrição..." value="${this.searchQuery}" style="flex: 1; min-width: 200px; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
          
          <select id="select-client" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; font-weight: 500;">
            <option value="ALL" ${this.clientFilter === "ALL" ? "selected" : ""}>Todos os Clientes</option>
            ${safeClients.map(c => `<option value="${c.id}" ${this.clientFilter === c.id ? "selected" : ""}>${c.name}</option>`).join('')}
          </select>

          <select id="select-status" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
            <option value="ALL" ${this.statusFilter === "ALL" ? "selected" : ""}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === "ACTIVE" ? "selected" : ""}>Ativas</option>
            <option value="INACTIVE" ${this.statusFilter === "INACTIVE" ? "selected" : ""}>Inativas</option>
          </select>
        </div>

        <!-- Data Table -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
            <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
              <tr>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CLIENTE</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">NOME DA ÁREA</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">DESCRIÇÃO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">ATUALIZADO EM</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="6" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhuma área encontrada com os filtros selecionados.</td>
                </tr>
              ` : filtered.map(a => `
                <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                  <td style="padding: 12px 16px; font-weight: 600; color: var(--color-ink);">${clientMap.get(a.clientId) || a.clientId}</td>
                  <td style="padding: 12px 16px; font-weight: 600; color: var(--ia-primary);">${a.name}</td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary);">${a.description || "-"}</td>
                  <td style="padding: 12px 16px;">
                    <span style="background: ${a.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${a.status === 'ACTIVE' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                      ${a.status === 'ACTIVE' ? 'ATIVA' : 'INATIVA'}
                    </span>
                  </td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary); font-size: 0.8125rem;">
                    ${new Date(a.updatedAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td style="padding: 12px 16px; text-align: right; white-space: nowrap;">
                    <button class="btn-edit" data-id="${a.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 10px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px; font-weight: 500;">Editar</button>
                    ${a.status === 'ACTIVE' ? `
                      <button class="btn-inactivate" data-id="${a.id}" data-name="${a.name}" data-client="${a.clientId}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text); margin-right: 4px;">Inativar</button>
                    ` : ''}
                    <button class="btn-delete" data-id="${a.id}" data-name="${a.name}" data-client="${a.clientId}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 10px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-solid); font-weight: 600;">Excluir</button>
                  </td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>

        ${this.isModalOpen ? this.renderModal() : ""}
        ${this.deleteModal.isOpen ? this.renderDeleteModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  private renderDeleteModal(): string {
    const { title, message, error, action } = this.deleteModal;
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 440px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.15); text-align: center;">
          <div style="width: 48px; height: 48px; border-radius: 50%; background: var(--status-danger-bg); color: var(--status-danger-solid); display: flex; align-items: center; justify-content: center; font-size: 1.5rem; margin: 0 auto 16px auto;">
            ⚠
          </div>
          <h2 style="font-size: 1.2rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 10px 0;">${title}</h2>
          <p style="color: var(--color-text-secondary); font-size: 0.9rem; margin-bottom: 20px; line-height: 1.5;">${message}</p>
          ${error ? `<div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px; text-align: left;">${error}</div>` : ''}
          <div style="display: flex; justify-content: center; gap: 12px;">
            <button id="btn-cancel-delete-action" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 20px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
            <button id="btn-confirm-delete-action" style="background: var(--status-danger-solid); border: none; border-radius: 6px; padding: 8px 20px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: pointer;">
              ${action === 'delete' ? 'Sim, Excluir' : 'Sim, Inativar'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderModal(): string {
    const isEdit = !!this.editingArea;
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 480px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">${isEdit ? 'Editar Área' : 'Nova Área'}</h2>
            <button id="btn-close-modal" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>

          ${this.modalErrorMessage ? `
            <div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-bg); color: var(--status-danger-solid); padding: 10px 14px; border-radius: 6px; font-size: 0.85rem; margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
              <span>⚠ ${this.modalErrorMessage}</span>
            </div>
          ` : ''}

          <form id="form-area" style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">
                Cliente *
              </label>
              ${isEdit ? `
                <input type="text" value="${this.clients.find(c => c.id === this.editingArea?.clientId)?.name || this.editingArea?.clientId}" disabled style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: var(--color-surface-secondary); color: var(--color-text-secondary);">
                <input type="hidden" id="modal-client" value="${this.editingArea?.clientId}">
              ` : `
                <select id="modal-client" required style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: var(--color-surface-primary);">
                  <option value="">Selecione o Cliente...</option>
                  ${this.clients.map(c => `<option value="${c.id}">${c.name}</option>`).join('')}
                </select>
              `}
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Nome da Área / Setor *</label>
              <input type="text" id="modal-name" value="${this.editingArea?.name || ''}" required placeholder="Ex: Utilidades, Destilação, Tancagem" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Descrição Detalhada</label>
              <textarea id="modal-description" rows="3" placeholder="Informações de localização ou observações adicionais..." style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; resize: vertical;">${this.editingArea?.description || ''}</textarea>
            </div>

            <div style="display: flex; ${isEdit ? 'justify-content: space-between;' : 'justify-content: flex-end;'} align-items: center; margin-top: 8px; border-top: 1px solid var(--color-graphite-200); padding-top: 14px;">
              ${isEdit ? `
                <button type="button" id="btn-modal-delete" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 8px 14px; font-size: 0.85rem; font-weight: 600; color: var(--status-danger-solid); cursor: pointer;">Excluir Área</button>
              ` : ''}
              <div style="display: flex; gap: 8px;">
                <button type="button" id="btn-cancel-modal" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
                <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Salvar</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#select-client")?.addEventListener("change", (e) => {
      this.clientFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#select-status")?.addEventListener("change", (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#btn-new-area")?.addEventListener("click", () => {
      this.editingArea = null;
      this.modalErrorMessage = null;
      this.isModalOpen = true;
      this.render();
    });

    this.querySelectorAll(".btn-edit").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.editingArea = this.areas.find(a => a.id === id) || null;
        this.modalErrorMessage = null;
        this.isModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-inactivate").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id") || "";
        const name = (e.currentTarget as HTMLElement).getAttribute("data-name") || "esta área";
        const clientId = (e.currentTarget as HTMLElement).getAttribute("data-client") || "";
        this.deleteModal = {
          isOpen: true,
          id,
          name,
          clientId,
          title: "Inativar Área Operacional",
          message: `Deseja realmente inativar a área "${name}"? Ela não estará disponível para novos vínculos.`,
          action: "inactivate",
          error: null
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-delete").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id") || "";
        const name = (e.currentTarget as HTMLElement).getAttribute("data-name") || "esta área";
        const clientId = (e.currentTarget as HTMLElement).getAttribute("data-client") || "";
        this.deleteModal = {
          isOpen: true,
          id,
          name,
          clientId,
          title: "Excluir Área Operacional",
          message: `Deseja realmente excluir a área "${name}"? Esta operação é irreversível.`,
          action: "delete",
          error: null
        };
        this.render();
      });
    });

    this.querySelector("#btn-modal-delete")?.addEventListener("click", () => {
      if (!this.editingArea) return;
      const area = this.editingArea;
      this.isModalOpen = false;
      this.deleteModal = {
        isOpen: true,
        id: area.id,
        name: area.name,
        clientId: area.clientId,
        title: "Excluir Área Operacional",
        message: `Deseja realmente excluir a área "${area.name}"? Esta operação é irreversível.`,
        action: "delete",
        error: null
      };
      this.render();
    });

    this.querySelector("#btn-cancel-delete-action")?.addEventListener("click", () => {
      this.deleteModal.isOpen = false;
      this.render();
    });

    this.querySelector("#btn-confirm-delete-action")?.addEventListener("click", async () => {
      const { id, clientId, action } = this.deleteModal;
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: clientId || ctx.activeClientId || null,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (action === "delete") {
          await platform.adminService.deleteArea(dummyContext, id);
        } else {
          await platform.adminService.inactivateArea(dummyContext, id);
        }
        this.deleteModal.isOpen = false;
        await this.loadData();
      } catch (err: any) {
        this.deleteModal.error = err.safeMessage || err.message || "Erro ao processar ação.";
        this.render();
      }
    });

    this.querySelector("#btn-close-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#form-area")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const clientSelect = this.querySelector("#modal-client") as HTMLSelectElement | HTMLInputElement;
      const clientId = clientSelect?.value?.trim();

      if (!clientId) {
        this.modalErrorMessage = "SELECIONE O CLIENTE.";
        this.render();
        return;
      }

      const name = (this.querySelector("#modal-name") as HTMLInputElement).value;
      const description = (this.querySelector("#modal-description") as HTMLTextAreaElement).value;

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: clientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (this.editingArea) {
          await platform.adminService.updateArea(dummyContext, {
            ...this.editingArea,
            name,
            description
          });
        } else {
          await platform.adminService.createArea(dummyContext, {
            clientId,
            name,
            description
          });
        }
        this.isModalOpen = false;
        this.modalErrorMessage = null;
        await this.loadData();
      } catch (err: any) {
        this.modalErrorMessage = err.message || "Erro ao salvar área.";
        this.render();
      }
    });
  }
}

if (!customElements.get("app-admin-areas-page")) {
  customElements.define("app-admin-areas-page", AdminAreasPage);
}



